const BYTES_PER_KIB = 1024
const BYTES_PER_MIB = BYTES_PER_KIB * BYTES_PER_KIB

// Fallback when VITE_WS_MAX_SIZE_BYTES is unset. Keep in sync with the backend
// WS_MAX_SIZE limit (app/backend/custom_uvicorn_worker.py) — if a deployment
// lowers the backend limit, set VITE_WS_MAX_SIZE_BYTES to match so this guard
// doesn't admit uploads the WebSocket will later reject.
const DEFAULT_CHAT_WEBSOCKET_MAX_MESSAGE_BYTES = 200 * BYTES_PER_MIB

export const CHAT_UPLOAD_RESERVED_MESSAGE_OVERHEAD_BYTES = 6 * BYTES_PER_MIB
export const CHAT_WEBSOCKET_MAX_MESSAGE_BYTES = resolveChatWebSocketMaxMessageBytes()
export const CHAT_UPLOAD_MAX_RAW_BYTES = calculateChatUploadMaxRawBytes(
  CHAT_WEBSOCKET_MAX_MESSAGE_BYTES
)

const CHAT_UPLOAD_MAX_ESTIMATED_PAYLOAD_BYTES = Math.max(
  0,
  CHAT_WEBSOCKET_MAX_MESSAGE_BYTES - CHAT_UPLOAD_RESERVED_MESSAGE_OVERHEAD_BYTES
)
const FILE_MESSAGE_OVERHEAD_BYTES = 256
const textEncoder = new TextEncoder()

type FileSizeInput = Pick<File, "name" | "size">

export type ChatUploadSizeError = {
  estimatedPayloadBytes: number
  fileName?: string
  fileSizeLabel?: string
  maxSizeLabel: string
  reason: "combined" | "single-file"
  totalRawBytes: number
  totalSizeLabel: string
}

export function estimateBase64EncodedBytes(rawBytes: number): number {
  return Math.ceil(rawBytes / 3) * 4
}

export function resolveChatWebSocketMaxMessageBytes(
  configuredValue: unknown = import.meta.env.VITE_WS_MAX_SIZE_BYTES
): number {
  if (configuredValue === undefined || configuredValue === null || configuredValue === "") {
    return DEFAULT_CHAT_WEBSOCKET_MAX_MESSAGE_BYTES
  }

  const parsedValue = Number(configuredValue)
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return DEFAULT_CHAT_WEBSOCKET_MAX_MESSAGE_BYTES
  }

  return Math.floor(parsedValue)
}

export function calculateChatUploadMaxRawBytes(webSocketMaxMessageBytes: number): number {
  const maxEstimatedPayloadBytes = Math.max(
    0,
    webSocketMaxMessageBytes - CHAT_UPLOAD_RESERVED_MESSAGE_OVERHEAD_BYTES
  )
  const rawBytesBeforeEncoding = (maxEstimatedPayloadBytes * 3) / 4

  return Math.floor(rawBytesBeforeEncoding / BYTES_PER_MIB) * BYTES_PER_MIB
}

export function estimateChatUploadPayloadBytes(files: FileSizeInput[]): number {
  return files.reduce((total, file) => total + estimateFilePayloadBytes(file), 0)
}

export function getChatUploadSizeError(files: FileSizeInput[]): ChatUploadSizeError | null {
  const totalRawBytes = files.reduce((total, file) => total + file.size, 0)
  const estimatedPayloadBytes = estimateChatUploadPayloadBytes(files)
  const oversizedFile = files.find(
    (file) =>
      file.size > CHAT_UPLOAD_MAX_RAW_BYTES ||
      estimateFilePayloadBytes(file) > CHAT_UPLOAD_MAX_ESTIMATED_PAYLOAD_BYTES
  )

  if (oversizedFile) {
    return {
      estimatedPayloadBytes,
      fileName: oversizedFile.name,
      fileSizeLabel: formatChatUploadBytes(oversizedFile.size),
      maxSizeLabel: formatChatUploadBytes(CHAT_UPLOAD_MAX_RAW_BYTES),
      reason: "single-file",
      totalRawBytes,
      totalSizeLabel: formatChatUploadBytes(totalRawBytes),
    }
  }

  if (
    totalRawBytes <= CHAT_UPLOAD_MAX_RAW_BYTES &&
    estimatedPayloadBytes <= CHAT_UPLOAD_MAX_ESTIMATED_PAYLOAD_BYTES
  ) {
    return null
  }

  return {
    estimatedPayloadBytes,
    maxSizeLabel: formatChatUploadBytes(CHAT_UPLOAD_MAX_RAW_BYTES),
    reason: "combined",
    totalRawBytes,
    totalSizeLabel: formatChatUploadBytes(totalRawBytes),
  }
}

export function isChatUploadWithinSizeLimit(files: FileSizeInput[]): boolean {
  return getChatUploadSizeError(files) === null
}

function estimateFilePayloadBytes(file: FileSizeInput): number {
  return (
    estimateBase64EncodedBytes(file.size) +
    textEncoder.encode(file.name).length +
    FILE_MESSAGE_OVERHEAD_BYTES
  )
}

function formatChatUploadBytes(bytes: number): string {
  if (bytes === 0) return "0 B"

  const units = ["B", "KB", "MB", "GB"]
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(BYTES_PER_KIB)),
    units.length - 1
  )
  const value = bytes / BYTES_PER_KIB ** unitIndex
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(1)

  return `${formatted} ${units[unitIndex]}`
}
